import { useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { useHubPetParent } from './HubPetParentContext';
import { identifyPetParent } from './analytics';

/**
 * A Hub `page_view` that cannot outrun `identify()`.
 *
 * Use this instead of `usePageView` for anything inside the Hub tree. The public
 * lesson pages render outside it (no HubPetParentProvider) and keep `usePageView`;
 * they identify from the `?pp=` hashid instead.
 *
 * Why it exists: `MixpanelIdentifier` can only identify once the pet parent query
 * resolves, but a screen's `page_view` fires the moment it mounts. On a direct
 * entry — a Zendesk link or an emailed deep link, where the app boots straight
 * onto the page — the event won every time and went out with an anonymous
 * `$device:` distinct_id and no `email` super property. Lesson pages showed it
 * most clearly because their content comes from the bundled catalog, so nothing
 * else made them wait, but every Hub screen had the same race.
 *
 * Two things fix it. Holding the event until `loading` is false, and identifying
 * here rather than trusting `MixpanelIdentifier` to have run first. The identify
 * is idempotent, so the duplicate call is free, and it means this does not depend
 * on sibling effect ordering inside the Hub tree.
 *
 * If the query fails we still fire, anonymously, once `loading` clears — losing
 * the pageview outright would be the worse trade.
 *
 * `props`/`deps`/`enabled` behave exactly as in `usePageView`: pass `deps` when a
 * property arrives asynchronously, and `enabled` to hold the event until the data
 * it describes is actually present.
 */
export default function useHubPageView(
  props?: Record<string, unknown>,
  deps: unknown[] = [],
  enabled = true,
): void {
  const { petParent, loading } = useHubPetParent();
  const petParentId = petParent?.id;

  useEffect(() => {
    if (!enabled || loading) return;
    if (petParentId !== undefined && petParentId !== null) identifyPetParent(petParentId);
    mixpanel.track_pageview(props || {}, { event_name: 'page_view' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loading, petParentId, ...deps]);
}
